import {
  PIXEL_PAST_RESCUE_MAX_FALL_SPEED,
  PIXEL_PAST_RESCUE_WORLD_WIDTH,
  pixelPastRectsOverlap,
} from './pixelPastRescueLayout';

/**
 * @typedef {{ x: number, y: number, w: number, h: number }} Rect
 */

/**
 * @param {Rect} box
 * @param {Rect[]} solids
 * @param {number} dx
 * @returns {number}
 */
export function moveBoxHorizontal(box, solids, dx) {
  if (dx === 0) {
    return 0;
  }
  const next = { ...box, x: box.x + dx };
  for (const solid of solids) {
    if (!pixelPastRectsOverlap(next, solid)) {
      continue;
    }
    if (dx > 0) {
      next.x = solid.x - box.w;
    } else {
      next.x = solid.x + solid.w;
    }
  }
  next.x = Math.max(0, Math.min(PIXEL_PAST_RESCUE_WORLD_WIDTH - box.w, next.x));
  return next.x - box.x;
}

/**
 * @param {Rect} box
 * @param {Rect[]} solids
 * @param {number} dy
 * @returns {{ deltaY: number, onGround: boolean }}
 */
export function moveBoxVertical(box, solids, dy) {
  if (dy === 0) {
    return { deltaY: 0, onGround: false };
  }
  const next = { ...box, y: box.y + dy };
  let onGround = false;
  for (const solid of solids) {
    if (!pixelPastRectsOverlap(next, solid)) {
      continue;
    }
    if (dy > 0) {
      next.y = solid.y - box.h;
      onGround = true;
    } else {
      next.y = solid.y + solid.h;
    }
  }
  const applied = next.y - box.y;
  return { deltaY: applied, onGround: onGround || (dy > 0 && applied < dy) };
}

/**
 * @param {Rect} player
 * @param {import('./pixelPastRescueLayout').PixelPastMonster[]} monsters
 * @returns {import('./pixelPastRescueLayout').PixelPastMonster[]}
 */
export function stepPixelPastMonsters(monsters) {
  return monsters.map((monster) => {
    let x = monster.x + monster.vx;
    let vx = monster.vx;
    if (x <= monster.minX) {
      x = monster.minX;
      vx = Math.abs(monster.vx);
    } else if (x + monster.w >= monster.maxX) {
      x = monster.maxX - monster.w;
      vx = -Math.abs(monster.vx);
    }
    return { ...monster, x, vx };
  });
}

/**
 * @param {number} vy
 * @param {number} riseGravity
 * @param {number} fallGravity
 * @returns {number}
 */
export function applyPixelPastGravity(vy, riseGravity, fallGravity) {
  if (vy < 0) {
    return vy + riseGravity;
  }
  return Math.min(PIXEL_PAST_RESCUE_MAX_FALL_SPEED, vy + fallGravity);
}

/**
 * Ice movement: accelerate with input, coast with friction when keys released or after landing.
 * @param {number} vx
 * @param {number} moveDir -1, 0, or 1
 * @param {boolean} onGround
 * @param {number} accel
 * @param {number} maxSpeed
 * @param {number} groundFriction
 * @param {number} airFriction
 * @returns {number}
 */
export function applyPixelPastIceHorizontalVelocity(
  vx,
  moveDir,
  onGround,
  accel,
  maxSpeed,
  groundFriction,
  airFriction
) {
  let next = vx;
  if (moveDir !== 0) {
    next += moveDir * accel;
    if (next > maxSpeed) {
      next = maxSpeed;
    } else if (next < -maxSpeed) {
      next = -maxSpeed;
    }
    return next;
  }
  const friction = onGround ? groundFriction : airFriction;
  next *= friction;
  if (Math.abs(next) < 0.035) {
    return 0;
  }
  return next;
}
